import { useCallback } from "react";

import type { MagazineAnalyticsSetting_DesignPageQuery } from "@/lib/gql/__generated__/graphql";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { toGuestPageVisibilitySettingsVariables } from "@/components/features-new/guest-page-visibility";

import { viewModelToInput } from "./converter/view-model-to-input";
import { MagazineAnalyticsSettingUpdate_DesignPage_Mutation } from "./mutation";

import type { MagazineAnalyticsSettingFormModel } from "../../../type";

type Props = {
	viewModel: MagazineAnalyticsSettingFormModel["ViewModel"];
	/** 取得した公開設定（マガジン以外のページはこの値のまま送る） */
	guestPageVisibilitySettings: MagazineAnalyticsSetting_DesignPageQuery["guestPageVisibilitySettings"];
};

export const useMagazineAnalyticsUpdateMutation = ({ viewModel, guestPageVisibilitySettings }: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const { mutationFunc } = useCustomMutation(MagazineAnalyticsSettingUpdate_DesignPage_Mutation);

	/** 保存する。保存できたかどうかを返す（失敗したときは、画面を保存済みの状態に戻さないため） */
	const onUpdate = useCallback(async (): Promise<boolean> => {
		showLoadingScreenCallback();

		const input = viewModelToInput({ viewModel });
		const guestPageVisibilitySettingsVariables = toGuestPageVisibilitySettingsVariables({
			saved: guestPageVisibilitySettings,
			visibilityKey: "magazineVisibility",
			visibility: viewModel.guestPageVisibility.value,
		});

		const { errors } = await mutationFunc({
			variables: {
				input,
				...guestPageVisibilitySettingsVariables,
			},
		}).finally(hideLoadingScreenCallback);

		if (errors) {
			return false;
		}
		notifyUserEventInfo({
			title: "マガジンの設定を保存しました",
		});
		return true;
	}, [guestPageVisibilitySettings, hideLoadingScreenCallback, mutationFunc, showLoadingScreenCallback, viewModel]);

	return {
		onUpdate,
	};
};
